'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { EntityListRowRenderErrorPanel } from './EntityListRowRenderErrorPanel';

export type EntityListRowErrorBoundaryLabels = {
  title: string;
  tryAgain: string;
};

type Props = {
  children: ReactNode;
  labels: EntityListRowErrorBoundaryLabels;
  className?: string;
  /** When set, used instead of the default card fallback (e.g. a single table cell). */
  renderFallback?: (args: { error: Error; reset: () => void }) => ReactNode;
};

type State = { error: Error | null };

/**
 * Catches render errors for a single entity list row so one bad cell does not
 * crash the whole list.
 */
export class EntityListRowErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (process.env.NODE_ENV !== 'production') {
      console.error('EntityListRowErrorBoundary', error, info.componentStack);
    }
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.renderFallback) {
        return this.props.renderFallback({
          error: this.state.error,
          reset: this.handleReset
        });
      }
      return (
        <EntityListRowRenderErrorPanel
          title={this.props.labels.title}
          message={this.state.error.message}
          tryAgainLabel={this.props.labels.tryAgain}
          onTryAgain={this.handleReset}
          className={this.props.className}
        />
      );
    }
    return this.props.children;
  }
}
